import { useEffect, useMemo } from "react";
import { OuterContentContainer } from "@themes/pollinator/ui/OuterContentContainer";
import { SectionHeading } from "@themes/pollinator/ui/SectionHeading";
import { Type } from "@themes/pollinator/ui/Type";
import { Avatar } from "@themes/pollinator/ui/Avatar";
import { Loader } from "@themes/pollinator/ui/Loader";
import { useFetchUserCircles, isCircleVisible } from "@core/features/circle";
import {
  useFetchActions,
  useUpdateActionStatus,
  ACTION_STATUS,
  ACTION_TYPE_SLUG,
  type Action,
} from "@core/features/actions";
import { usePlatformStore, usePublicProfileSheet } from "@platform/store";
import { PublicProfileSheetContainer } from "@platform/features/programProfile";
import type { UserWithProfile } from "@core/features/auth";

export type Props = {
  /** Program ID for the circle */
  programId: number;
};

/**
 * Circle tab content for the Program page.
 * Shows the user's peer circle members if matched, otherwise shows a not-matched message.
 */
export function CircleTabContent({ programId }: Props) {
  const user = usePlatformStore((s) => s.user);
  const { open: openProfileSheet } = usePublicProfileSheet();

  const { data: circlesData, isLoading } = useFetchUserCircles({
    user,
    programId,
    isEnabled: !!user,
  });

  const { data: actionsData } = useFetchActions({
    programId,
    user,
    isEnabled: !!user,
  });

  const circleViewAction = useMemo<Action | null>(() => {
    return (
      actionsData?.actions?.find(
        (a: Action) => a.action_type?.slug === ACTION_TYPE_SLUG.CIRCLE_VIEW
      ) ?? null
    );
  }, [actionsData?.actions]);

  const { mutate: markCircleViewCompleted } = useUpdateActionStatus({
    actionId: circleViewAction?.id ?? 0,
    status: ACTION_STATUS.COMPLETED,
    currentStatus: circleViewAction?.current_status?.name,
  });

  useEffect(() => {
    if (!circleViewAction) return;
    markCircleViewCompleted();
  }, [circleViewAction?.id, markCircleViewCompleted]);

  const visibleCircles = (circlesData?.circles ?? []).filter(isCircleVisible);
  const members = visibleCircles.flatMap((circle) => circle.users ?? []);

  return (
    <>
      <OuterContentContainer className="flex flex-1" innerClassName="gap-8">
        {isLoading ? (
          <SectionHeading title="Your Peer Cohort">
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          </SectionHeading>
        ) : members.length === 0 ? (
          <SectionHeading
            title="Your Peer Cohort"
            description="Once we match you, you’ll be able to see your cohort members here."
          />
        ) : (
          <SectionHeading
            title="Your Peer Cohort"
            description="See who’s in your peer cohort, and click on members to learn more about them via their profile."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {members.map((member: UserWithProfile) => (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => openProfileSheet(member.id)}
                  className="flex flex-row gap-4 text-left hover:bg-brand-50 transition-colors cursor-pointer"
                >
                  <Avatar
                    image={member.profile_photo}
                    size={48}
                    alt={`${member.first_name} ${member.last_name}`}
                  />
                  <div className="flex flex-col gap-1 flex-1">
                    <Type variant="h5">
                      {member.first_name} {member.last_name}
                    </Type>
                    {member.pronouns && (
                      <Type variant="caption">{member.pronouns}</Type>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </SectionHeading>
        )}
      </OuterContentContainer>
      <PublicProfileSheetContainer programId={programId} />
    </>
  );
}

export default CircleTabContent;
